import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

interface ExitIntentPopupProps {
  onClose?: () => void;
}

export const ExitIntentPopup = ({ onClose }: ExitIntentPopupProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShown && !sessionStorage.getItem('exitIntentShown')) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitIntentShown', 'true');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasShown]);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handleClaim = () => {
    navigate('/find');
    handleClose();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            onClick={handleClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-full max-w-lg mx-4"
          >
            <div className="bg-gradient-to-br from-background via-background to-primary/5 rounded-2xl shadow-2xl border-2 border-primary/20 overflow-hidden">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-10"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="p-8 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
                    <div className="relative bg-primary/10 p-4 rounded-full">
                      <Gift className="h-12 w-12 text-primary" />
                    </div>
                  </div>
                </div>

                <Badge className="mb-4 text-sm py-1.5 px-4">
                  ⏰ Limited Time Offer
                </Badge>

                <h2 className="text-3xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                  Wait! Don't Go Yet
                </h2>
                
                <p className="text-lg text-muted-foreground mb-6">
                  Get <span className="text-primary font-bold text-2xl">20% OFF</span> your first therapy session
                </p>

                <div className="bg-muted/50 rounded-lg p-4 mb-6 space-y-2 text-sm">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>No subscription required</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>5,000+ verified therapists</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Sessions from $25</span>
                  </div>
                </div>

                <Button
                  size="lg"
                  className="w-full text-lg py-6 shadow-lg hover:shadow-xl group"
                  onClick={handleClaim}
                >
                  Claim My 20% Discount
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>

                <p className="text-xs text-muted-foreground mt-4">
                  Offer expires in 24 hours • One-time use only
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
